import { makeAutoObservable } from "mobx";
import RootStore from "../../../../../stores/RootStore";
import ServiceStore from "../../../../../stores/ServiceStore";
import {
  Contact,
  Account,
  Opportunity,
  Activity
} from "types/common/ServiceStoreTypes";
import { ContactsListStore } from "../../ContactsList.Store";

export class ContactDetailStore {
  rootStore: RootStore;

  // Observable state
  contact: Contact | null = null;
  contactId: number | null = null;
  loading = false;
  error: string | null = null;

  // Related data
  opportunities: Opportunity[] = [];
  activities: Activity[] = [];

  // Loading states for related data
  loadingOpportunities = false;
  loadingActivities = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Service store access
  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  get accountsStore(): ServiceStore {
    return this.rootStore.accountsStore as ServiceStore;
  }

  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get activitiesStore(): ServiceStore {
    return this.rootStore.activitiesStore as ServiceStore;
  }

  // Data loading methods
  async loadContact(id: number) {
    this.contactId = id;
    this.loading = true;
    this.error = null;

    try {
      // First load all base entities from ServiceStores to ensure relational data is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.contactsStore.find(),
        this.accountsStore.find(),
        this.opportunitiesStore.find(),
        this.activitiesStore.find(),
      ]);

      // Then find the specific contact
      const result = await this.contactsStore.find({
        query: { contactId: id },
      });
      const contact = result?.data?.[0] as unknown as Contact;

      if (contact) {
        this.contact = contact;

        // Load related data in parallel
        await Promise.all([this.loadOpportunities(), this.loadActivities()]);
      } else {
        this.error = "Contact not found";
      }
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to load contact";
      console.error("Failed to load contact:", error);
    } finally {
      this.loading = false;
    }
  }

  private async loadOpportunities() {
    if (!this.contact?.contactId) return;

    this.loadingOpportunities = true;
    try {
      const result = await this.opportunitiesStore.find({
        query: {
          contactId: this.contact.contactId,
          $sort: { createdAt: -1 },
          $limit: 50,
        },
      });
      this.opportunities = result?.data as unknown as Opportunity[];
    } catch (error) {
      console.error("Failed to load opportunities:", error);
    } finally {
      this.loadingOpportunities = false;
    }
  }

  private async loadActivities() {
    if (!this.contact?.contactId) return;

    this.loadingActivities = true;
    try {
      const result = await this.activitiesStore.find({
        query: {
          contactId: this.contact.contactId,
          $sort: { createdAt: -1 },
          $limit: 100,
        },
      });
      this.activities = result?.data as unknown as Activity[];
    } catch (error) {
      console.error("Failed to load activities:", error);
    } finally {
      this.loadingActivities = false;
    }
  }

  // Contact management methods
  async updateContact(updates: Partial<Contact>) {
    if (!this.contact) return;

    try {
      const updated = (await this.contactsStore.patch(this.contact.contactId, {
        ...updates,
        updatedAt: new Date().toISOString(),
      })) as unknown as Contact;

      this.contact = { ...this.contact, ...updated };

      // Log activity
      try {
        const updateActivity: Partial<Activity> = {
          contactId: this.contact.contactId,
          activityType: "Update",
          subject: "Contact updated",
          description: `Contact updated: ${this.contact.firstName} ${this.contact.lastName}`,
          status: "Completed",
          priority: "Low",
          completedDate: new Date().toISOString(),
          notes: JSON.stringify(updates),
          followUpRequired: false,
        };

        await this.activitiesStore.create(updateActivity);

        // Reload activities to show the update
        await this.loadActivities();
      } catch (activityError) {
        console.error("Failed to log activity:", activityError);
      }

      return updated;
    } catch (error) {
      console.error("Failed to update contact:", error);
      throw error;
    }
  }

  async deleteContact() {
    if (!this.contact) return;

    try {
      await this.contactsStore.remove(this.contact.contactId);

      // Log activity before clearing the contact
      try {
        const deleteActivity: Partial<Activity> = {
          contactId: this.contact.contactId,
          activityType: "Delete",
          subject: "Contact deleted",
          description: `Contact deleted: ${this.contact.firstName} ${this.contact.lastName}`,
          status: "Completed",
          priority: "Low",
          completedDate: new Date().toISOString(),
          followUpRequired: false,
        };

        await this.activitiesStore.create(deleteActivity);
      } catch (activityError) {
        console.error("Failed to log activity:", activityError);
      }

      // Clear the contact data
      this.contact = null;
      this.opportunities = [];
      this.activities = [];
    } catch (error) {
      console.error("Failed to delete contact:", error);
      throw error;
    }
  }

  // Create related records
  async createOpportunity(opportunityData: Record<string, unknown>) {
    if (!this.contact) return;

    try {
      const opportunity = (await this.opportunitiesStore.create({
        ...opportunityData,
        contactId: this.contact.contactId,
        accountId: this.contact.accountId,
      })) as unknown as Opportunity;

      // Add to local list
      this.opportunities.unshift(opportunity);

      // Log activity
      try {
        const createActivity: Partial<Activity> = {
          contactId: this.contact.contactId,
          opportunityId: opportunity.opportunityId,
          activityType: "Create",
          subject: "Opportunity created",
          description: `Opportunity created: ${opportunityData.opportunityName}`,
          status: "Completed",
          priority: "Medium",
          completedDate: new Date().toISOString(),
          followUpRequired: false,
        };

        await this.activitiesStore.create(createActivity);

        await this.loadActivities();
      } catch (activityError) {
        console.error("Failed to log activity:", activityError);
      }

      return opportunity;
    } catch (error) {
      console.error("Failed to create opportunity:", error);
      throw error;
    }
  }

  async logActivity(activityData: Partial<Activity>) {
    if (!this.contact) return;

    try {
      const activity = (await this.activitiesStore.create({
        ...activityData,
        contactId: this.contact.contactId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })) as unknown as Activity;

      // Add to local list
      this.activities.unshift(activity);
      return activity;
    } catch (error) {
      console.error("Failed to log activity:", error);
      throw error;
    }
  }

  // Communication methods
  async sendEmail(emailData: { to?: string; subject?: string; body?: string }) {
    if (!this.contact) return;

    try {
      // This would integrate with email service
      const emailActivity: Partial<Activity> = {
        contactId: this.contact.contactId,
        activityType: "Email",
        subject: emailData.subject || "Email sent",
        description: `Email sent to ${emailData.to || this.contact.email}`,
        status: "Completed",
        priority: "Medium",
        completedDate: new Date().toISOString(),
        notes: emailData.body,
        followUpRequired: false,
      };

      await this.logActivity(emailActivity);
      return true;
    } catch (error) {
      console.error("Failed to send email:", error);
      throw error;
    }
  }

  async logCall(callData: {
    duration?: number;
    notes?: string;
    outcome?: string;
  }) {
    if (!this.contact) return;

    try {
      const callActivity: Partial<Activity> = {
        contactId: this.contact.contactId,
        activityType: "Call",
        subject: callData.outcome || "Phone call completed",
        description: `Phone call with ${this.contactFullName}`,
        status: "Completed",
        priority: "Medium",
        completedDate: new Date().toISOString(),
        duration: callData.duration,
        notes: callData.notes,
        followUpRequired: false,
      };

      await this.logActivity(callActivity);
      return true;
    } catch (error) {
      console.error("Failed to log call:", error);
      throw error;
    }
  }

  // Utility getters
  get account(): Account | null {
    console.log("Getting account for contact:", this.dialogContact);
    if (!this.dialogContact?.accountId) return null;

    const accountsStore = this.rootStore.accountsStore as ServiceStore;
    const account = accountsStore.entities.find((acc: unknown) => {
      const accountData = acc as { accountId: number };
      return accountData.accountId === this.dialogContact!.accountId;
    });

    return account ? (account as unknown as Account) : null;
  }

  get contactFullName(): string {
    if (!this.contact) return "";
    return `${this.contact.firstName} ${this.contact.lastName}`;
  }

  get contactInitials(): string {
    if (!this.contact) return "";
    return `${this.contact.firstName.charAt(0)}${this.contact.lastName.charAt(
      0
    )}`;
  }

  get hasRelatedData(): boolean {
    return this.opportunities.length > 0 || this.activities.length > 0;
  }

  get recentActivities(): Activity[] {
    return this.activities.slice(0, 10);
  }

  get activeOpportunities(): Opportunity[] {
    return this.opportunities.filter(
      (opp: Opportunity) =>
        opp.salesStage !== "Closed Won" && opp.salesStage !== "Closed Lost"
    );
  }

  // Computed property for dialog contact
  get dialogContact(): Contact | null {
    const contactsListStore = this.rootStore
      .contactsListStore as ContactsListStore;
    const contactId = Number(
      this.rootStore.routerStore?.queryParams.dialogContext
    );

    if (!contactId || !contactsListStore?.contacts) {
      return null;
    }

    return (
      contactsListStore.contacts.find(
        (contact: Contact) => contact.contactId === contactId
      ) || null
    );
  }

  // Cleanup
  reset() {
    this.contact = null;
    this.contactId = null;
    this.opportunities = [];
    this.activities = [];
    this.loading = false;
    this.error = null;
    this.loadingOpportunities = false;
    this.loadingActivities = false;
  }

  dispose() {
    this.reset();
  }
}

export default ContactDetailStore;
